import { supabase } from '../lib/supabase';
import { getTenantConfig, getSenderInfo } from './shopEmailService';

/**
 * Interface pour les données d'email de confirmation de commande e-learning
 */
interface ElearningOrderEmailData {
  to: string;
  name: string;
  orderCode: string;
  orderDate: string;
  orderItems: {
    title: string;
    price: number;
    instructor_name?: string;
    duration_hours?: number;
    thumbnail_url?: string;
  }[];
  totalAmount: number;
}

/**
 * Envoie un email de confirmation pour une commande e-learning
 */
export const sendElearningOrderConfirmationEmail = async (
  orderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('[elearningEmailService] Début de l\'envoi d\'email de confirmation pour la commande e-learning:', orderId);
    
    // Récupérer les détails de la commande et les éléments en parallèle pour accélérer
    const [orderResponse, itemsResponse] = await Promise.all([
      supabase.from('elearn_orders').select('*').eq('id', orderId).single(),
      supabase.from('elearn_order_items').select(`
        *,
        course:course_id(
          id,
          title,
          duration_hours,
          thumbnail_url,
          instructor:instructor_id(name)
        )
      `).eq('order_id', orderId)
    ]);
    
    const { data: order, error: orderError } = orderResponse;
    const { data: orderItems, error: itemsError } = itemsResponse;
      
    // Vérifier les erreurs
    if (orderError || !order) {
      console.error('[elearningEmailService] Erreur lors de la récupération de la commande:', orderError?.message || 'Commande introuvable');
      throw new Error(`Erreur lors de la récupération de la commande: ${orderError?.message || 'Commande introuvable'}`);
    }
    
    if (itemsError) {
      console.error('[elearningEmailService] Erreur lors de la récupération des éléments de la commande:', itemsError.message);
      throw new Error(`Erreur lors de la récupération des éléments de la commande: ${itemsError.message}`);
    }
    
    console.log('[elearningEmailService] Détails de la commande et éléments récupérés:', orderItems?.length || 0, 'cours');

    // Récupérer les informations du tenant et du sender en parallèle
    const [tenant, sender] = await Promise.all([
      getTenantConfig(),
      getSenderInfo('elearning') // Utiliser un sender spécifique pour e-learning ou fallback sur 'default'
    ]);

    // Préparer les données des cours pour l'email
    const formattedItems = (orderItems || []).map((item: any) => ({
      title: item.course?.title || 'Cours',
      price: item.price_at_purchase,
      instructor_name: item.course?.instructor?.name,
      duration_hours: item.course?.duration_hours,
      thumbnail_url: item.course?.thumbnail_url || ''
    }));
    
    console.log('[elearningEmailService] Cours formatés pour l\'email:', formattedItems.length);

    // Formater la date de commande
    const orderDate = new Date(order.created_at).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Préparer le contenu HTML pour les cours
    const coursesHtml = formattedItems.map((item: any) => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">
          ${item.thumbnail_url ? `<img src="${item.thumbnail_url}" alt="${item.title}" style="width: 50px; height: 50px; object-fit: cover;">` : ''}
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">
          <div>${item.title}</div>
          ${item.instructor_name ? `<div style="font-size: 12px; color: #666;">Par ${item.instructor_name}</div>` : ''}
          ${item.duration_hours ? `<div style="font-size: 12px; color: #666;">${item.duration_hours}h de contenu</div>` : ''}
        </td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">${item.price.toLocaleString('fr-FR')} FCFA</td>
      </tr>
    `).join('');

    // Préparer le contenu texte
    const textContent = `
Bonjour ${order.customer_name},

Nous vous remercions pour votre achat de cours sur ${tenant.name}.

Détails de la commande #${order.order_number} (${orderDate}) :

${formattedItems.map((item: any) => `- ${item.title}${item.instructor_name ? ` par ${item.instructor_name}` : ''} : ${item.price.toLocaleString('fr-FR')} FCFA`).join('\n')}

Total : ${order.total_amount.toLocaleString('fr-FR')} FCFA

Statut : ${order.status === 'completed' ? 'Payée' : 'En attente de paiement'}

Vous pouvez accéder à vos cours dès maintenant en vous connectant à votre espace étudiant.

Cordialement,
L'équipe de ${tenant.name}
`;

    // Préparer le contenu HTML
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Confirmation de votre commande</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="text-align: center; margin-bottom: 20px;">
    ${tenant.logo ? `<img src="${tenant.logo}" alt="${tenant.name}" style="max-width: 150px;">` : `<h1>${tenant.name}</h1>`}
  </div>
  
  <div style="background-color: #f9f9f9; border-radius: 5px; padding: 20px; margin-bottom: 20px;">
    <h2 style="color: #4CAF50; margin-top: 0;">Merci pour votre achat !</h2>
    <p>Bonjour ${order.customer_name},</p>
    <p>Nous vous remercions pour votre achat de cours sur notre plateforme. Votre commande a été traitée avec succès.</p>
    
    <div style="background-color: #fff; border-radius: 5px; padding: 15px; margin: 15px 0;">
      <h3 style="margin-top: 0;">Détails de la commande #${order.order_number}</h3>
      <p style="color: #666; font-size: 14px;">Date: ${orderDate}</p>
      
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 10px; border-bottom: 2px solid #eee;"></th>
            <th style="text-align: left; padding: 10px; border-bottom: 2px solid #eee;">Cours</th>
            <th style="text-align: right; padding: 10px; border-bottom: 2px solid #eee;">Prix</th>
          </tr>
        </thead>
        <tbody>
          ${coursesHtml}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2" style="text-align: right; padding: 10px; font-weight: bold;">Total</td>
            <td style="text-align: right; padding: 10px; font-weight: bold;">${order.total_amount.toLocaleString('fr-FR')} FCFA</td>
          </tr>
        </tfoot>
      </table>
    </div>
    
    <div style="background-color: #e8f5e9; border-radius: 5px; padding: 15px; margin-top: 20px;">
      <h3 style="color: #2E7D32; margin-top: 0;">Prochaines étapes</h3>
      <p>Vous pouvez accéder à vos cours dès maintenant en vous connectant à votre espace étudiant.</p>
      <div style="text-align: center; margin-top: 15px;">
        <a href="https://${tenant.domain_name}/elearning/student/dashboard" style="background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block;">Accéder à mes cours</a>
      </div>
    </div>
  </div>
  
  <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
    <p>Cet email a été envoyé par ${tenant.name}</p>
    <p>© ${new Date().getFullYear()} ${tenant.name}. Tous droits réservés.</p>
  </div>
</body>
</html>
`;

    // URL de la fonction Edge
    const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
    
    // Utiliser la clé d'API Supabase pour l'authentification
    const { VITE_SUPABASE_KEY } = import.meta.env;
    
    console.log('[elearningEmailService] Envoi de l\'email via la fonction Edge:', edgeFunctionUrl);
    console.log('[elearningEmailService] Destinataire:', order.customer_email);
    console.log('[elearningEmailService] Sujet:', `Confirmation de votre commande #${order.order_number} - ${tenant.name}`);
    
    // Envoyer l'email via la fonction Edge de Supabase avec fetch
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VITE_SUPABASE_KEY}`
      },
      body: JSON.stringify({
        to: order.customer_email,
        toName: order.customer_name,
        subject: `Confirmation de votre commande #${order.order_number} - ${tenant.name}`,
        htmlContent: htmlContent,
        textContent: textContent,
        fromEmail: sender.email,
        fromName: sender.name,
        messageId: 0
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[elearningEmailService] Erreur lors de l\'envoi de l\'email:', errorText);
      throw new Error(`Erreur lors de l'envoi de l'email: ${errorText}`);
    }
    
    const emailResponse = await response.json();
    
    if (!emailResponse.success) {
      console.error('[elearningEmailService] Erreur dans la réponse de la fonction Edge:', emailResponse);
      throw new Error(`Erreur lors de l'envoi de l'email: ${emailResponse.error || 'Erreur inconnue'}`);
    }

    console.log('[elearningEmailService] Email de confirmation envoyé avec succès:', emailResponse);
    
    return {
      success: true
    };
  } catch (error) {
    console.error('[elearningEmailService] Erreur:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Une erreur inconnue est survenue'
    };
  }
};
